/**
 * @file InvestmentProfile.jsx
 * @description Manages new user setup and profile creation
 * 
 * @section Responsibilities
 * - Collect user information
 * - Handle document uploads
 * - Validate profile data
 * - Track onboarding progress
 * 
 * @section Dependencies
 * - Profile Management: User data handling
 * - Document Service: File handling
 * - Validation Service: Data validation
 * 
 * @section Integration Points
 * - Profile API: Data submission
 * - Document Service: File handling
 * - Onboarding Flow: Progress tracking
 * 
 * @section State Management
 * - profileData: User information
 * - validationState: Form validation
 * - progress: Onboarding progress
 * 
 * @section Events
 * - onProfileUpdate: Profile changes
 * - onDocumentUpload: File handling
 * - onValidation: Form validation
 * 
 * @section Props
 * @prop {Object} initialData - Initial profile data
 * @prop {Function} onComplete - Completion callback
 */

import React, { useEffect, useState } from "react";
import { Grid } from "@mui/material";
import SelectInput from "../../../components/forms/SelectInput";
import TextInput from "../../../components/forms/TextInput";

function InvestmentProfile({ sendInvestmentProfile }) {
  const YEARS_EXPERIENCE = [
    { value: "0", text: "0" },
    { value: "1", text: "1-2 years" },
    { value: "2", text: "3-5 years" },
    { value: "3", text: "5+ years" },
  ];

  const TOTAL_NETWORTH = [
    { value: "0-50000", text: "$0 - $50,000" },
    { value: "50001-100000", text: "$50,001 - $100,000" },
    { value: "100001-200000", text: "$100,001 - $200,000" },
    { value: "200001-500000", text: "$200,001 - $500,000" },
    { value: "500001-1000000", text: "$500,001 - $1,000,000" },
    { value: "1000001-5000000", text: "$1,000,001 - $5,000,000" },
    { value: "5000001-9999999", text: "$5,000,000+" },
  ];

  const LIQUID_NETWORTH = [
    { value: "0-50000", text: "$0 - $50,000" },
    { value: "50001-100000", text: "$50,001 - $100,000" },
    { value: "100001-200000", text: "$100,001 - $200,000" },
    { value: "200001-500000", text: "$200,001 - $500,000" },
    { value: "500001-1000000", text: "$500,001 - $1,000,000" },
    { value: "1000001-5000000", text: "$1,000,001 - $5,000,000" },
    { value: "5000001-9999999", text: "$5,000,000+" },
  ];

  const ANNUAL_INCOME = [
    { value: "0-25000", text: "$0 - $25,000" },
    { value: "25001-50000", text: "$25,001 - $50,000" },
    { value: "50001-100000", text: "$50,001 - $100,000" },
    { value: "100001-200000", text: "$100,001 - $200,000" },
    { value: "200001-300000", text: "$200,001 - $300,000" },
    { value: "300001-500000", text: "$300,001 - $500,000" },
    { value: "500001-1200000", text: "$500,001 - $1,200,000" },
    { value: "1200001-9999999", text: "$1,200,000+" },
  ];

  //TODO Handle descriptions
  const RISK_TOLERANCE = [
    {
      text: "High/Speculation",
      description:
        "I am willing to accept maximum risk to my initial principal to aggressively seek maximum returns, and I understand I could lose most, or all, of the money invested.",
      objective: "SPECULATION",
      value: "HIGH",
    },
    {
      text: "Medium/Growth",
      description:
        "I am willing to accept high risk to my principal, including high volatility, to seek high returns over time, and I understand I could lose a substantial amount of the money invested.",
      objective: "GROWTH",
      value: "MEDIUM",
    },
    {
      text: "Low/Income",
      description:
        "I am willing to accept low risk to my initial principal, including low volatility, to seek a modest level of portfolio returns.",
      objective: "INCOME",
      value: "LOW",
    },
  ];

  const INVESTMENT_EXPERIENCE = [
    { value: "NONE", text: "0 Years" },
    { value: "LIMITED", text: "1 to 2 Years" },
    { value: "GOOD", text: "3 to 5 Years" },
    { value: "EXTENSIVE", text: "More than 5 Years" },
  ];

  const emptyData = {
    totalNetWorthUSD: {
      min: 0,
      max: 0,
    },
    liquidNetWorthUSD: {
      min: 0,
      max: 0,
    },
    annualIncomeUSD: {
      min: 0,
      max: 0,
    },
    investmentObjective: "",
    riskTolerance: "",
    investmentExperience: "",
    federalTaxBracketPercent: "",
    stockExperience: "",
    optionExperience: "",
  };

  const [data, setData] = useState(emptyData);
  const [totalNetWorthUSD, setTotalNetWorthUSD] = useState("");
  const [liquidNetWorthUSD, setLiquidNetWorthUSD] = useState("");
  const [annualIncomeUSD, setAnnualIncomeUSDD] = useState("");

  const handleChange = (field, value) => {
    setData((prevState) => ({
      ...prevState,
      [field]: value,
    }));
  };

  const handleNestedChange = (field, subField, value) => {
    setData((prevState) => ({
      ...prevState,
      [field]: {
        ...prevState[field],
        [subField]: value,
      },
    }));
  };

  const handleMinMax = (field, value) => {
    const numbers = value.split("-");
    const min = parseInt(numbers[0], 10);
    const max = parseInt(numbers[1], 10);

    switch (field) {
      case "totalNetWorthUSD":
        setTotalNetWorthUSD(value);
        handleNestedChange("totalNetWorthUSD", "min", min);
        handleNestedChange("totalNetWorthUSD", "max", max);
        break;
      case "liquidNetWorthUSD":
        setLiquidNetWorthUSD(value);
        handleNestedChange("liquidNetWorthUSD", "min", min);
        handleNestedChange("liquidNetWorthUSD", "max", max);
        break;
      case "annualIncomeUSD":
        setAnnualIncomeUSDD(value);
        handleNestedChange("annualIncomeUSD", "min", min);
        handleNestedChange("annualIncomeUSD", "max", max);
        break;
      default:
        console.warn(`Unhandled field: ${field}`);
    }
  };

  useEffect(() => {
    sendInvestmentProfile(data);
  }, [data]);

  useEffect(() => {
    const foundOption = RISK_TOLERANCE.find(
      (option) => option.value === data.riskTolerance
    );
    if (foundOption) {
      handleChange("investmentObjective", foundOption.objective);
    }
  }, [data.riskTolerance]);

  return (
    <>
      <Grid container rowSpacing={4} columnSpacing={{ xs: 0, sm: 2, md: 12 }}>
        <Grid item xs={12} sm={4}>
          <div className="form-field">
            <SelectInput
              customInput={{
                label: "Total Networth",
                name: "totalNetWorthUSD",
                value: totalNetWorthUSD,
                action: (e) => handleMinMax("totalNetWorthUSD", e.target.value),
                tooltipBool: false,
              }}
              data={TOTAL_NETWORTH}
            />
          </div>
        </Grid>
        <Grid item xs={12} sm={4}>
          <div className="form-field">
            <SelectInput
              customInput={{
                label: "Liquid Networth",
                name: "liquidNetWorthUSD",
                value: liquidNetWorthUSD,
                action: (e) =>
                  handleMinMax("liquidNetWorthUSD", e.target.value),
                tooltipBool: false,
              }}
              data={LIQUID_NETWORTH}
            />
          </div>
        </Grid>
        <Grid item xs={12} sm={4}>
          <div className="form-field">
            <SelectInput
              customInput={{
                label: "Annual Income",
                name: "annualIncomeUSD",
                value: annualIncomeUSD,
                action: (e) => handleMinMax("annualIncomeUSD", e.target.value),
                tooltipBool: false,
              }}
              data={ANNUAL_INCOME}
            ></SelectInput>
          </div>
        </Grid>
      </Grid>
      <Grid container rowSpacing={4} columnSpacing={{ xs: 0, sm: 2, md: 12 }}>
        <Grid item xs={12} sm={6}>
          <div className="form-field">
            <SelectInput
              customInput={{
                label: "Risk Tolerance",
                name: "riskTolerance",
                value: data.riskTolerance,
                action: (e) => handleChange("riskTolerance", e.target.value),
                tooltipBool: false,
              }}
              data={RISK_TOLERANCE}
            ></SelectInput>
          </div>
        </Grid>
        <Grid item xs={12} sm={6}>
          <div className="form-field">
            <SelectInput
              customInput={{
                label: "Investment experience",
                name: "investmentExperience",
                value: data.investmentExperience,
                action: (e) =>
                  handleChange("investmentExperience", e.target.value),
                tooltipBool: false,
              }}
              data={INVESTMENT_EXPERIENCE}
            ></SelectInput>
          </div>
        </Grid>
      </Grid>
      <Grid container rowSpacing={4} columnSpacing={{ xs: 0, sm: 2, md: 12 }}>
        <Grid item xs={12} sm={6}>
          <div className="form-field">
            <SelectInput
              customInput={{
                label: "Years of Stock Trading Experience",
                name: "stockExperience",
                value: data.stockExperience,
                action: (e) => handleChange("stockExperience", e.target.value),
                placeholder: 5,
                type: "number",
                step: 1,
                min: 1,
                tooltipBool: false,
              }}
              data={YEARS_EXPERIENCE}
            />
          </div>
        </Grid>
        <Grid item xs={12} sm={6}>
          <div className="form-field">
            <SelectInput
              customInput={{
                label: "Years of Option Trading Experience",
                name: "optionExperience",
                value: data.optionExperience,
                step: 1,
                action: (e) => handleChange("optionExperience", e.target.value),
                placeholder: 5,
                type: "number",
                step: 1,
                min: 1,
                tooltipBool: false,
              }}
              data={YEARS_EXPERIENCE}
            />
          </div>
        </Grid>
      </Grid>
      <Grid container rowSpacing={4} columnSpacing={{ xs: 0, sm: 2, md: 12 }}>
        <Grid item xs={12} sm={6}>
          <div className="form-field">
            <TextInput
              customInput={{
                label: "Federal Tax Bracket Percent",
                name: "federalTaxBracketPercent",
                type: "number",
                step: 1,
                value: data.federalTaxBracketPercent,
                action: (e) =>
                  handleChange("federalTaxBracketPercent", e.target.value),
                tooltipBool: false,
              }}
            />
          </div>
        </Grid>
      </Grid>
    </>
  );
}

export default InvestmentProfile;
